import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { FormEvent, useMemo, useState } from "react";
import { apiVariable } from "../../api/apiVariable";
import {
  assignEmployeePtoSubPolicy,
  createPtoAccrualSubPolicy,
  getEmployeePtoSubPolicyAssignments,
  getPtoAccrualSubPolicies,
} from "../../api/ptoAccrualSubPolicyService";
import useAuth from "../../hooks/useAuth";
import { EmployeeDto } from "../../interfaces/HR/employee";

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.message ??
      error.response?.data?.title ??
      error.message
    );
  }
  return error instanceof Error
    ? error.message
    : "An unexpected error occurred.";
};

const dateOnly = (value: string) => value.slice(0, 10);

const isEligibleForPtoSubPolicy = (employee: EmployeeDto) =>
  employee.deliveryUnit?.trim().toLowerCase() === "us" &&
  employee.contractType?.trim().toLowerCase() === "w2";

const PtoAccrualSubPoliciesPage = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [entitlement, setEntitlement] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [subPolicyId, setSubPolicyId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");

  const policiesQuery = useQuery({
    queryKey: ["ptoAccrualSubPolicies"],
    enabled: Boolean(token),
    queryFn: () => getPtoAccrualSubPolicies(token!),
  });
  const employeesQuery = useQuery({
    queryKey: ["employees", "ptoSubPolicies", "active"],
    enabled: Boolean(token),
    queryFn: async () => {
      const { data } = await axios.get<EmployeeDto[]>(
        `${apiVariable}/getAllEmployees`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { areActive: true },
        },
      );
      return data.filter(isEligibleForPtoSubPolicy);
    },
    staleTime: 5 * 60 * 1000,
  });
  const historyQuery = useQuery({
    queryKey: ["ptoSubPolicyAssignments", employeeId],
    enabled: Boolean(token && employeeId),
    queryFn: () => getEmployeePtoSubPolicyAssignments(token!, employeeId),
  });

  const sortedHistory = useMemo(
    () =>
      [...(historyQuery.data ?? [])].sort((a, b) =>
        b.effectiveDate.localeCompare(a.effectiveDate),
      ),
    [historyQuery.data],
  );
  const selectedEmployee =
    employeesQuery.data?.find((employee) => employee.id === employeeId) ?? null;
  const selectedPolicy =
    policiesQuery.data?.find((policy) => policy.id === subPolicyId) ?? null;

  const createMutation = useMutation({
    mutationFn: () =>
      createPtoAccrualSubPolicy(token!, {
        name: name.trim(),
        annualEntitlementDays: Number(entitlement),
      }),
    onSuccess: async () => {
      setName("");
      setEntitlement("");
      await queryClient.invalidateQueries({
        queryKey: ["ptoAccrualSubPolicies"],
      });
    },
  });
  const assignMutation = useMutation({
    mutationFn: () =>
      assignEmployeePtoSubPolicy(token!, employeeId, {
        ptoAccrualSubPolicyId: subPolicyId,
        effectiveDate: `${effectiveDate}T00:00:00`,
      }),
    onSuccess: async () => {
      setSubPolicyId("");
      setEffectiveDate("");
      await queryClient.invalidateQueries({
        queryKey: ["ptoSubPolicyAssignments", employeeId],
      });
    },
  });

  const submitPolicy = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() && Number(entitlement) > 0) createMutation.mutate();
  };
  const submitAssignment = (event: FormEvent) => {
    event.preventDefault();
    if (
      selectedEmployee &&
      isEligibleForPtoSubPolicy(selectedEmployee) &&
      subPolicyId &&
      effectiveDate
    ) {
      assignMutation.mutate();
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        PTO Accrual Sub-Policies
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Configure annual PTO entitlements and schedule effective-dated employee
        assignments.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 2.5 }}>
            <Stack
              flexWrap={{ xs: "wrap", lg: "nowrap" }}
              useFlexGap
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Available sub-policies</Typography>
              <Chip
                label={`${policiesQuery.data?.length ?? 0} policies`}
                size="small"
              />
            </Stack>
            {policiesQuery.isLoading ? (
              <CircularProgress size={28} />
            ) : policiesQuery.isError ? (
              <Alert severity="error">Unable to load PTO sub-policies.</Alert>
            ) : (
              <Grid container spacing={2}>
                {policiesQuery.data?.map((policy) => (
                  <Grid item xs={12} sm={6} key={policy.id}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Stack
                          flexWrap={{ xs: "wrap", lg: "nowrap" }}
                          useFlexGap
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Typography variant="subtitle1" fontWeight={600}>
                            {policy.name}
                          </Typography>
                          {policy.isDefault && (
                            <Chip
                              label="Default"
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                        <Typography variant="h4" sx={{ mt: 2 }}>
                          {policy.annualEntitlementDays}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          days per year
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Create a sub-policy
            </Typography>
            <Box component="form" onSubmit={submitPolicy}>
              <Stack spacing={2}>
                <TextField
                  label="Policy name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  inputProps={{ maxLength: 100 }}
                />
                <TextField
                  label="Annual entitlement (days)"
                  type="number"
                  value={entitlement}
                  onChange={(e) => setEntitlement(e.target.value)}
                  required
                  inputProps={{ min: 0.01, step: 0.01 }}
                />
                {createMutation.isError && (
                  <Alert severity="error">
                    {getErrorMessage(createMutation.error)}
                  </Alert>
                )}
                {createMutation.isSuccess && (
                  <Alert severity="success">Sub-policy created.</Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={
                    createMutation.isPending ||
                    !name.trim() ||
                    Number(entitlement) <= 0
                  }
                >
                  Create sub-policy
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6" gutterBottom>
              Assign an employee
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              The existing balance is retained. The current policy remains in
              effect through the day before this date.
            </Alert>
            <Box component="form" onSubmit={submitAssignment}>
              <Stack spacing={2}>
                <Autocomplete
                  options={employeesQuery.data ?? []}
                  loading={employeesQuery.isLoading}
                  value={selectedEmployee}
                  onChange={(_, value) => setEmployeeId(value?.id ?? "")}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  getOptionLabel={(employee) =>
                    `${employee.firstName} ${employee.lastName}`
                  }
                  noOptionsText="No active US W-2 employees found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Employee"
                      helperText="Only active US delivery unit employees with a W-2 contract are eligible."
                    />
                  )}
                />
                <Autocomplete
                  options={policiesQuery.data ?? []}
                  loading={policiesQuery.isLoading}
                  value={selectedPolicy}
                  onChange={(_, value) => setSubPolicyId(value?.id ?? "")}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  getOptionLabel={(policy) =>
                    `${policy.name} — ${policy.annualEntitlementDays} days/year`
                  }
                  renderInput={(params) => (
                    <TextField {...params} required label="PTO sub-policy" />
                  )}
                />
                <TextField
                  label="Effective date"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
                {assignMutation.isError && (
                  <Alert severity="error">
                    {getErrorMessage(assignMutation.error)}
                  </Alert>
                )}
                {assignMutation.isSuccess && (
                  <Alert severity="success">
                    Assignment saved without changing the employee's accrued
                    balance.
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    assignMutation.isPending ||
                    !employeeId ||
                    !subPolicyId ||
                    !effectiveDate
                  }
                >
                  Save assignment
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Assignment history
            </Typography>
            {!employeeId ? (
              <Typography color="text.secondary">
                Select an employee to view their history.
              </Typography>
            ) : historyQuery.isLoading ? (
              <CircularProgress size={28} />
            ) : historyQuery.isError ? (
              <Alert severity="error">Unable to load assignment history.</Alert>
            ) : sortedHistory.length === 0 ? (
              <Alert severity="info">No sub-policy assignments found.</Alert>
            ) : (
              <Stack spacing={1.5}>
                {sortedHistory.map((assignment, index) => (
                  <Card variant="outlined" key={assignment.id}>
                    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                      <Stack
                        flexWrap={{ xs: "wrap", lg: "nowrap" }}
                        useFlexGap
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography fontWeight={600}>
                            {assignment.subPolicyName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Effective {dateOnly(assignment.effectiveDate)} ·{" "}
                            {assignment.annualEntitlementDays} days/year
                          </Typography>
                        </Box>
                        {index === 0 && (
                          <Chip label="Latest" color="success" size="small" />
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PtoAccrualSubPoliciesPage;
